import asyncForever from 'async/forever';
import Platform from './platform';

export const IPFS_PREFIX = 'https://ipfs.io/ipfs';
export const MEDIA_PREFIX = '/';

export default {
  setDebugLogger,
  noop,
  log,
  errorLog,
  errorQuietLog,
  debugLog,
  getEnvironment,
  shallowEqual,
  deepEqual,
  deepClone,
  urlCat,
  between,
  timeFormat,
  oneShot,
  topMargin,
  bottomMargin,
  bottomCornerMargin,
  safeTop,
  safeBottom,
  isDesktop,
  isLandscape,
  capitalize,
  unPromise,
  repeat,
  filterInPlace,
  preventDefault,
  stopPropagation,
  stopAll,
  isDebug,
  isSimulator,
  now,
  urlHashGet,
  urlQueryGet,
  jsonParse,
  makeContentUrl,
  IPFS_PREFIX,
  numberToMoney,
  numberToDisplay,
  intToDisplay,
  spaceCamel,
  getVersion,
};

type Logger = (...args: any) => void;

function noop() {}

let g_logger: Logger | null = null;
function setDebugLogger(logger: Logger) {
  g_logger = logger;
}
function log(...args: any) {
  console.log(...args);
}
function errorLog(...args: any) {
  console.log(...args);
  g_logger && g_logger(...args);
}
function errorQuietLog(...args: any) {
  console.log(...args);
  g_logger && g_logger(...args);
}
function debugLog(...args: any) {
  console.log(...args);
  g_logger && g_logger(...args);
}

export function getEnvironment(): string {
  let environment = 'prod';
  if (Platform && Platform.RNInfoManifestBridge) {
    const { RNInfoManifestBridge } = Platform;
    (console as any).reportErrorsAsExceptions = false;

    const bundleId = RNInfoManifestBridge.bundleIdentifier;
    const is_dev =
      bundleId.indexOf('dev') !== -1 ||
      bundleId.indexOf('local') !== -1 ||
      bundleId.indexOf('test') !== -1;
    const is_staging = bundleId.indexOf('staging') !== -1;

    if (is_dev) {
      environment = 'dev';
    } else if (is_staging) {
      environment = 'staging';
    }
  } else {
    if (window.location && window.location.href) {
      const is_dev = window.location.href.indexOf(':3') >= 0;
      if (is_dev) {
        environment = 'dev';
      }
    }
  }
  return environment;
}
export function isSimulator(): boolean {
  return (
    Platform &&
    Platform.RNInfoManifestBridge &&
    Platform.RNInfoManifestBridge.DTPlatformName &&
    Platform.RNInfoManifestBridge.DTPlatformName.indexOf('simulator') !== -1
  );
}

function shallowEqual(objA, objB) {
  if (objA === objB) {
    return true;
  }

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  var keysA = Object.keys(objA);
  var keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  var bHasOwnProperty = Object.prototype.hasOwnProperty.bind(objB);
  for (var i = 0; i < keysA.length; i++) {
    if (!bHasOwnProperty(keysA[i]) || objA[keysA[i]] !== objB[keysA[i]]) {
      return false;
    }
  }

  return true;
}

function deepEqual(x, y) {
  if (x === y) {
    return true;
  } else if (x === null || y === null) {
    return false;
  } else {
    if (typeof x === 'object' && typeof y === 'object') {
      for (let p in x) {
        if (x.hasOwnProperty(p)) {
          if (!y.hasOwnProperty(p)) {
            return false;
          } else if (!deepEqual(x[p], y[p])) {
            return false;
          }
        }
      }

      for (let p in y) {
        if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) {
          return false;
        }
      }
    } else {
      return false;
    }
    return true;
  }
}

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function urlCat() {
  let url = '';
  for (let i = 0; i < arguments.length; i++) {
    const part = arguments[0];
    if (url.length > 0 && url.charAt(url.length - 1) !== '/') {
      url += '/';
    }
    if (part.charAt(0) === '/') {
      url += part.slice(1);
    } else {
      url += part;
    }
  }
  return url;
}

function between(value, min, max) {
  return Math.max(Math.min(value, max), min);
}

function timeFormat(time) {
  let seconds = Math.floor(time / 1000);

  let minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  let hours = Math.floor(minutes / 60);
  minutes -= hours * 60;
  return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
}

function pad(i) {
  return i < 10 ? '0' + i : i;
}

const g_one_shot_map = {};
function oneShot(key) {
  const ret = !(key in g_one_shot_map);
  if (ret) {
    g_one_shot_map[key] = true;
  }
  return ret;
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

let g_is_iphoneX;
function _iphoneX() {
  if (g_is_iphoneX === undefined) {
    const ios =
      /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

    const ratio = window.devicePixelRatio || 1;

    const width = window.screen.width * ratio;
    const height = window.screen.height * ratio;

    if (ios && width === 1125 && height === 2436) {
      g_is_iphoneX = true;
    } else {
      g_is_iphoneX = false;
    }
  }
  return g_is_iphoneX;
}

function topMargin() {
  return _iphoneX() ? 44 : 0;
}
function bottomMargin() {
  return _iphoneX() ? 34 : 0;
}
function bottomCornerMargin() {
  return _iphoneX() ? 30 : 0;
}
function safeTop(): number {
  let ret = 0;
  if (Platform.OS !== 'web') {
    const insets =
      Platform.initialWindowMetrics && Platform.initialWindowMetrics.insets;
    const top = (insets && insets.top) || 0;
    ret =
      top + (Platform.OS === 'android' ? Platform.StatusBar.currentHeight : 0);
  }
  return ret;
}

function safeBottom(): number {
  let ret = 0;
  if (Platform.OS !== 'web') {
    const insets =
      Platform.initialWindowMetrics && Platform.initialWindowMetrics.insets;
    const bottom = (insets && insets.bottom) || 0;
    ret = bottom;
  }
  return ret;
}

function isDesktop() {
  const { width, height } = window.screen;
  return width >= 600 && height >= 500;
}
function isLandscape() {
  const { width, height } = window.screen;
  return width > height;
}

function unPromise(promise, done) {
  try {
    promise
      .then((...result) => {
        done(null, ...result);
      })
      .catch((err) => {
        done(err);
      });
  } catch (e) {
    done(e);
  }
}

function repeat(callback, times, done) {
  if (!done) {
    done = function () {};
  }

  let count = 0;
  const BACKOFF_MS = 1 * 1000;

  asyncForever(
    (done) => {
      count++;
      if (count > times) {
        done('timeout');
      } else {
        callback((err) => {
          if (err) {
            setTimeout(done, BACKOFF_MS);
          } else {
            done('success');
          }
        });
      }
    },
    (err) => {
      if (err === 'success') {
        err = null;
      }
      done(err);
    }
  );
}

function filterInPlace(array, callback) {
  for (let i = array.length - 1; i >= 0; i--) {
    if (!callback(array[i], i)) {
      array.splice(i, 1);
    }
  }
}

function stopPropagation(e) {
  e && e.stopPropagation && e.stopPropagation();
}
function preventDefault(e) {
  e && e.preventDefault && e.preventDefault();
}
function stopAll(e) {
  stopPropagation(e);
  preventDefault(e);
}

function isDebug() {
  const origin = window && window.location && window.location.origin;
  const is_local = origin && origin.indexOf(':30') !== -1;

  return is_local;
}
function now() {
  if (window && window.performance && window.performance.now) {
    return window.performance.now();
  } else {
    return Date.now();
  }
}
export function urlHashGet(key) {
  let ret;
  const { hash } = window.location;
  if (hash) {
    const hash_s = hash.slice(1);
    const map = {};

    hash_s.split('&').forEach((part) => {
      const k_v = part.split('=');
      if (k_v.length === 2) {
        map[k_v[0]] = k_v[1];
      }
    });
    ret = map[key];
  }
  return ret;
}
export function urlQueryGet(key) {
  let ret;
  const search = window && window.location && window.location.search;
  if (search) {
    const kv_s = search.slice(1);
    const map: any = {};

    kv_s.split('&').forEach((part) => {
      const k_v = part.split('=');
      if (k_v.length === 2) {
        map[k_v[0]] = k_v[1];
      }
    });
    ret = map[key];
  }
  return ret;
}
export function jsonParse(s: string) {
  let ret;
  try {
    ret = JSON.parse(s);
  } catch (e) {
    // noop
  }
  return ret;
}
export function makeContentUrl(source: string, image_cached?: boolean) {
  let ret = source;
  if (source) {
    if (source.indexOf('ipfs:') === 0) {
      const cid = source.slice(7);
      if (image_cached) {
        ret = MEDIA_PREFIX + cid;
      } else {
        ret = IPFS_PREFIX + '/' + cid;
      }
    } else if (source.indexOf('http') !== 0) {
      ret = MEDIA_PREFIX + source;
    }
  }
  return ret;
}
const USD_FORMATER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
export function numberToMoney(num: number): string {
  return USD_FORMATER.format(num);
}
const SMALL_FORMATER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 8,
});
const MIDDLE_FORMATER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});
const LARGE_FORMATER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
export function numberToDisplay(num: number): string {
  let ret;
  if (num > 1000) {
    ret = LARGE_FORMATER.format(num);
  } else if (num < 1) {
    ret = SMALL_FORMATER.format(num);
  } else {
    ret = MIDDLE_FORMATER.format(num);
  }
  return ret;
}
function intToDisplay(num: number): string {
  let ret;
  if (num > 1000) {
    ret = LARGE_FORMATER.format(num);
  } else if (num < 1) {
    ret = num.toFixed();
  } else {
    ret = MIDDLE_FORMATER.format(num);
  }
  return ret;
}
export function spaceCamel(s: string): string {
  let ret = '';
  let did_space = true;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c >= 'A' && c <= 'Z') {
      if (!did_space) {
        ret += ' ';
      }
    } else {
      did_space = false;
    }
    ret += c;
  }
  return ret;
}
export function getVersion() {
  let ret;
  if (Platform && Platform.RNInfoManifestBridge) {
    ret = Platform.RNInfoManifestBridge.shortVersion;
  } else {
    ret = process.env.VERSION;
  }
  return ret;
}
